import React, { useState, useMemo, useCallback } from "react";
import {
  Box,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Badge,
} from "@chakra-ui/react";
import { SupplierList } from "./SupplierList";
import { ManualSupplierList } from "./ManualSupplierList";
import { SelectedSupplier, PartQuantity } from "./utils/partsCalculationUtils";
import {
  SuprsendRequestStatus,
  SuprsendPerEntityStatus,
} from "./utils/broadcastUtils";
import { GaraazInputSearchBar } from "@svan-garaaz/reactsharedlib";
import { SupplierDiscoverySortOptions, OfflineSupplierEntry } from "./types";
import { AddToSupplierBucketOptions } from "./utils/assignQtyForSupplierParts";

interface TabbedSupplierListProps {
  // Smart Search props
  customerSupplierAssociations: any[];
  customerGeoPoint: { latitude: number; longitude: number };
  preFacetedPartNumbers: PartQuantity[];
  remainingParts: PartQuantity[];
  handleSelectListItem: (item: any, supplierParts?: any[]) => void;
  handleAddToSelectedSupplierBucket: (
    item: any,
    supplierParts?: any[],
    options?: AddToSupplierBucketOptions,
  ) => void;
  onSelectionClose: () => void;
  typesenseInstantsearchAdapter: any;
  supplierDiscoveryCollectionName: string;
  geoDistanceFilter: string;
  supplier_discovery_parts_limit: number;
  selectedSuppliers: SelectedSupplier[];
  isFullyFulfilled: boolean;
  isBucketSaveAllowed: boolean;
  onSaveBucket?: () => void;
  setShowCreateAnotherBucket: (show: boolean) => void;
  supplierCityMap: Map<string, string>;
  isEstimateOrder: boolean;
  offlinePartAvailabilityDiscrepanciesPerSupplierMap?: Map<
    string,
    OfflineSupplierEntry
  >;

  // Manual List props
  filteredSuppliers: any[];
  onSelectManualSupplier: (supplier: any) => void;
  onAddToBroadcast?: (supplier: any) => void;
  broadcastSuppliers?: any[];
  isSendingBroadcast?: boolean;
  isBroadcastEnabled?: boolean;
  notificationRequestStatusBySupplier?: Record<string, SuprsendPerEntityStatus>;
  supplierInventoryDeltaQuantityMap?: Map<string, number>;
  supplierPartConfirmationQuantityMap?: Map<string, number>;
  /** 0 = Auto Discovered, 1 = Offline Discovery */
  activeTab: number;
  onActiveTabChange: (index: number) => void;
  orderId: string;
  orderNo: string;
}

const sortOptions = [
  { value: SupplierDiscoverySortOptions.DISTANCE, label: "Distance" },
  {
    value: SupplierDiscoverySortOptions.FULFILLEDPERCENTAGE,
    label: "Complete Fulfillment",
  },
  { value: SupplierDiscoverySortOptions.AVAILABILITY, label: "Part Coverage" },
  {
    value: SupplierDiscoverySortOptions.TOTALPARTSPERCENTAGE,
    label: "Quantity Coverage",
  },
];

export const TabbedSupplierList: React.FC<TabbedSupplierListProps> = ({
  customerSupplierAssociations,
  customerGeoPoint,
  preFacetedPartNumbers,
  remainingParts,
  handleSelectListItem,
  handleAddToSelectedSupplierBucket,
  onSelectionClose,
  typesenseInstantsearchAdapter,
  supplierDiscoveryCollectionName,
  geoDistanceFilter,
  supplier_discovery_parts_limit,
  selectedSuppliers,
  isFullyFulfilled,
  isBucketSaveAllowed,
  onSaveBucket,
  setShowCreateAnotherBucket,
  supplierCityMap,
  isEstimateOrder,
  offlinePartAvailabilityDiscrepanciesPerSupplierMap,
  filteredSuppliers,
  onSelectManualSupplier,
  onAddToBroadcast,
  broadcastSuppliers = [],
  isSendingBroadcast = false,
  isBroadcastEnabled = false,
  notificationRequestStatusBySupplier,
  supplierInventoryDeltaQuantityMap,
  supplierPartConfirmationQuantityMap,
  activeTab,
  onActiveTabChange,
  orderId,
  orderNo,
}) => {
  const [searchInput, setSearchInput] = useState("");
  const [sortField, setSortField] = useState<SupplierDiscoverySortOptions>(
    SupplierDiscoverySortOptions.DISTANCE,
  );

  const filteredManualSuppliers = useMemo(() => {
    if (!searchInput.trim()) {
      return filteredSuppliers;
    }
    const searchLower = searchInput.toLowerCase();
    return filteredSuppliers.filter((supplier: any) => {
      const name = supplier.businessName?.toLowerCase() ?? "";
      return name.includes(searchLower);
    });
  }, [filteredSuppliers, searchInput]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchInput(e.target.value);
    },
    [],
  );

  const searchPlaceholder =
    activeTab === 0 ? "Search supplier by name" : "Search manual supplier list";

  return (
    <Box justifySelf="center" flex={3.5} w="100%">
      <GaraazInputSearchBar
        searchInput={searchInput}
        onSearchChange={handleSearchChange}
        sortField={sortField}
        setSortField={setSortField}
        sortOptions={sortOptions}
        placeholder={searchPlaceholder}
        showSortControl={activeTab === 0}
      />
      <Tabs
        index={activeTab}
        onChange={onActiveTabChange}
        isLazy
        colorScheme="blue"
        variant="soft-rounded"
        mt={3}
      >
        <TabList bg="white" borderRadius="lg" p={1} mb={3} boxShadow="sm">
          <Tab
            fontWeight="600"
            fontSize="sm"
            _selected={{
              color: "white",
              bg: "blue.500",
            }}
            flex={1}
          >
            Auto Discovered
            <Badge
              ml={2}
              colorScheme="purple"
              fontSize="xs"
              bg={activeTab === 0 ? "purple.600" : "purple.100"}
              color={activeTab === 0 ? "white" : "purple.800"}
            >
              AI
            </Badge>
          </Tab>
          <Tab
            fontWeight="600"
            fontSize="sm"
            _selected={{
              color: "white",
              bg: "blue.500",
            }}
            flex={1}
          >
            Offline Discovery
            <Badge
              ml={2}
              colorScheme="gray"
              fontSize="xs"
              bg={activeTab === 1 ? "gray.600" : "gray.100"}
              color={activeTab === 1 ? "white" : "gray.800"}
            >
              {filteredManualSuppliers.length}
            </Badge>
          </Tab>
        </TabList>

        <TabPanels>
          <TabPanel p={0}>
            <SupplierList
              customerSupplierAssociations={customerSupplierAssociations}
              customerGeoPoint={customerGeoPoint}
              preFacetedPartNumbers={remainingParts}
              handleSelectListItem={handleSelectListItem}
              handleAddToSelectedSupplierBucket={
                handleAddToSelectedSupplierBucket
              }
              onSelectionClose={onSelectionClose}
              typesenseInstantsearchAdapter={typesenseInstantsearchAdapter}
              geoDistanceFilter={geoDistanceFilter}
              supplierDiscoveryCollectionName={supplierDiscoveryCollectionName}
              supplier_discovery_parts_limit={supplier_discovery_parts_limit}
              selectedSuppliers={selectedSuppliers}
              isFullyFulfilled={isFullyFulfilled}
              isBucketSaveAllowed={isBucketSaveAllowed}
              onSaveBucket={onSaveBucket}
              setShowCreateAnotherBucket={setShowCreateAnotherBucket}
              supplierCityMap={supplierCityMap}
              searchInput={searchInput}
              sortField={sortField}
              isEstimateOrder={isEstimateOrder}
              offlinePartAvailabilityDiscrepanciesPerSupplierMap={
                offlinePartAvailabilityDiscrepanciesPerSupplierMap
              }
              supplierInventoryDeltaQuantityMap={
                supplierInventoryDeltaQuantityMap
              }
              supplierPartConfirmationQuantityMap={
                supplierPartConfirmationQuantityMap
              }
              orderId={orderId}
              orderNo={orderNo}
            />
          </TabPanel>
          <TabPanel p={0}>
            <ManualSupplierList
              suppliers={filteredManualSuppliers}
              selectedSuppliersInBucket={selectedSuppliers}
              isOrderFullyFulfilled={isFullyFulfilled}
              onSelectSupplier={onSelectManualSupplier}
              onAddToBroadcast={onAddToBroadcast}
              broadcastSuppliers={broadcastSuppliers}
              isSendingBroadcast={isSendingBroadcast}
              isBroadcastEnabled={isBroadcastEnabled}
              notificationRequestStatusBySupplier={
                notificationRequestStatusBySupplier
              }
            />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};
